import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  contentChild,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  NgZone,
  OnDestroy,
  output,
  Output,
  signal,
  TemplateRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DragType } from '@app/enums';
import { EventModel } from '@reducers/orm/event/event.model';
import { OpenShiftModel } from '@reducers/orm/open-shift/open-shift.model';
import { RequiredShiftModel } from '@reducers/orm/required-shift/required-shift.model';
import { ScheduleModel } from '@reducers/orm/schedule/schedule.model';
import { ShiftModel } from '@reducers/orm/shift/shift.model';
import { TeamModel } from '@reducers/orm/team/team.model';
import { ScheduleItemModel } from '@reducers/store/schedule/schedule.n.model';
import { ScheduleItemActionsComponent } from '@sb/schedule/primitives';
import { UserColorBgPipe } from '@sb/shared/theme';
import { generateId } from '@sb/shared/util';
import { TooltipDirective } from '@sb/tooltip';
import { FreeDraggableDirective } from '@shared/drag-drop/directives/free-draggable.directive';
import { SFDragDropModule } from '@shared/drag-drop/sf-drag-drop.module';
import { useRepaintOnThemeChange } from '@shared/theme/use-repaint-on-theme-change';
import { Subscription } from 'rxjs';

import { ScheduleNEmployeeActionService } from '../../../../shared/schedule-actions/action-services/schedule-n-employee-action.service';
import { ScheduleNEventActionService } from '../../../../shared/schedule-actions/action-services/schedule-n-event-action.service';
import { ScheduleNOpenShiftActionService } from '../../../../shared/schedule-actions/action-services/schedule-n-open-shift-action.service';
import { ScheduleNRequiredShiftActionService } from '../../../../shared/schedule-actions/action-services/schedule-n-required-shift-action.service';
import { NDraggable } from '../../../../shared/schedule-actions/interfaces';
import { ScheduleNActionService } from '../../../../shared/schedule-actions/schedule-n-action.service';
import { ScheduleItemTooltipTemplateDirective } from './schedule-item-tooltip-template.directive';

const DEFAULT_ITEM_COLOR = '#2399e4';

const ACTIONS_ACTIVATION_EVENTS = ['mouseenter', 'focusin'];

@Component({
  selector: 'schedule-base-item',
  templateUrl: './schedule-base-item.component.html',
  imports: [NgClass, TooltipDirective, SFDragDropModule, UserColorBgPipe, ScheduleItemActionsComponent],
})
export class ScheduleBaseItemComponent implements AfterViewInit, OnDestroy {
  private scheduleActionService = inject(ScheduleNActionService);
  private cd = inject(ChangeDetectorRef);
  private zone = inject(NgZone);
  private router = inject(Router);
  private employeeActionService = inject(ScheduleNEmployeeActionService);
  private requiredshiftActionService = inject(ScheduleNRequiredShiftActionService);
  private openShiftActionService = inject(ScheduleNOpenShiftActionService);
  private eventActionService = inject(ScheduleNEventActionService);
  private route = inject(ActivatedRoute);

  @Input()
  scheduleItem: ScheduleItemModel;

  /**
   * Whether this item may offer find-cover. Decided by the owning template,
   * which holds the schedule and its conflicts; this component only renders it.
   */
  canFindCover = input(false);

  findCover = output<MouseEvent>();

  @Input()
  shift: ShiftModel | undefined;

  @Input()
  team: TeamModel | undefined;

  @Input()
  isConflicted: boolean;

  @Input()
  color: string | undefined;

  @Input()
  draggable?: NDraggable;

  @Input()
  shouldDisplayGhostWhenDraggingButNotCopying: boolean;

  @ViewChild(FreeDraggableDirective, { static: true })
  draggableComponent: FreeDraggableDirective;

  @ViewChild(TooltipDirective, { static: true })
  tooltip: TooltipDirective;

  item = viewChild.required<ElementRef<HTMLElement>>('item');

  tooltipTemplate = contentChild(ScheduleItemTooltipTemplateDirective, { read: TemplateRef });

  @Input()
  isDisabled: boolean;

  isLocked = input<boolean>(false);

  @Output()
  dragStart = new EventEmitter();
  @Output()
  dragEnd = new EventEmitter();

  @Output()
  onDelete = new EventEmitter();

  @Input()
  disableHoverButtons: boolean;

  @Input()
  mode: 'employee' | 'team';

  isDragging: boolean;
  isCopying: boolean;
  isDraggingGracePeriod = false;

  /**
   * The hover actions are mounted on the first hover or focus, then kept: a
   * schedule renders hundreds of items and most are never pointed at.
   */
  actionsActivated = signal(false);

  contentId = generateId('schedule-item-content');

  private dataSubs = new Subscription();
  private dragStartTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    useRepaintOnThemeChange();
  }

  resolvedColor(): string {
    return this.color ?? DEFAULT_ITEM_COLOR;
  }

  hasActions(): boolean {
    return !this.disableHoverButtons && !this.isLocked();
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      const itemElement = this.item().nativeElement;
      ACTIONS_ACTIVATION_EVENTS.forEach((eventName) => itemElement.addEventListener(eventName, this.#activateActions));

      this.dataSubs.add(
        this.draggableComponent.onDragStart.subscribe(() => {
          this.onDragStart();
          this.updateView();
        }),
      );

      this.dataSubs.add(
        this.draggableComponent.onDragEnd.subscribe(() => {
          this.onDragEnd();
          this.updateView();
        }),
      );
    });
  }

  updateView() {
    this.cd.detectChanges();
  }

  /**
   * Listens outside the zone so hovering across items costs no change detection;
   * the actions render inside it so their click handlers run in the zone.
   */
  #activateActions = () => {
    this.#removeActivationListeners();

    this.zone.run(() => {
      this.actionsActivated.set(true);
      this.updateView();
    });
  };

  #removeActivationListeners(): void {
    const itemElement = this.item().nativeElement;
    ACTIONS_ACTIVATION_EVENTS.forEach((eventName) => itemElement.removeEventListener(eventName, this.#activateActions));
  }

  private onDragStart() {
    this.isDragging = true;
    this.tooltip.hide();
    this.scheduleActionService.isDragging.next(this.draggable);
    this.dragStart.emit();

    // This should be done on the next stack, else you won't have a drag preview (dragging nothing)
    this.dragStartTimeout = setTimeout(() => {
      if (this.isDragging) {
        this.isDraggingGracePeriod = true;
        this.updateView();
      }
    }, 0);
  }

  private onDragEnd() {
    this.isDragging = false;
    this.isDraggingGracePeriod = false;
    this.isCopying = false;
    this.scheduleActionService.isDragging.next(null);
    this.dragEnd.emit();
  }

  ngOnDestroy(): void {
    if (this.tooltip) {
      this.tooltip.hide();
    }

    if (this.dragStartTimeout) {
      clearTimeout(this.dragStartTimeout);
    }

    this.#removeActivationListeners();

    this.dataSubs.unsubscribe();
  }

  edit(event: MouseEvent) {
    event.stopPropagation();
    this.zone.run(() => {
      switch (this.draggable.type) {
        case DragType.SCHEDULE: {
          const modalRouteSegments: (
            | string
            | {
                originator: string;
              }
          )[] = ['schedule-shift', this.scheduleItem.occurrence_id, 'edit'];

          // TODO because the way modal routes are setup, we don't have access to the original route params in a clean way
          // so we have to pass along the originator in the modal route
          if (this.route.snapshot.params?.['originator']) {
            modalRouteSegments.push({ originator: this.route.snapshot.params['originator'] });
          }

          this.router.navigate([
            '',
            {
              outlets: {
                modal: modalRouteSegments,
              },
            },
          ]);
          break;
        }
        case DragType.OPEN_SHIFT: {
          this.router.navigate([
            '',
            { outlets: { modal: ['schedule-open-shift', this.scheduleItem.occurrence_id, 'edit'] } },
          ]);
          break;
        }
        case DragType.REQUIRED_SHIFT: {
          this.router.navigate([
            '',
            {
              outlets: {
                modal: ['schedule-required-shift', this.scheduleItem.occurrence_id, 'edit'],
              },
            },
          ]);
          break;
        }
        case DragType.EVENT: {
          this.router.navigate(['', { outlets: { modal: ['event', this.scheduleItem.id, 'edit'] } }]);
          break;
        }
      }
    });
  }

  view(event: MouseEvent) {
    event.stopPropagation();
    switch (this.draggable.type) {
      case DragType.SCHEDULE: {
        this.router.navigate(['', { outlets: { modal: ['schedule-shift', this.scheduleItem.occurrence_id] } }]);
        break;
      }
      case DragType.OPEN_SHIFT: {
        this.router.navigate(['', { outlets: { modal: ['schedule-open-shift', this.scheduleItem.occurrence_id] } }]);
        break;
      }
      case DragType.REQUIRED_SHIFT: {
        this.router.navigate([
          '',
          { outlets: { modal: ['schedule-required-shift', this.scheduleItem.occurrence_id] } },
        ]);
        break;
      }
      case DragType.EVENT: {
        this.router.navigate(['', { outlets: { modal: ['event', this.scheduleItem.id] } }]);
        break;
      }
    }
  }

  create(event: MouseEvent) {
    event.stopPropagation();
    switch (this.draggable.type) {
      case DragType.SCHEDULE: {
        const team = this.team;
        if (!team) {
          break;
        }

        const shift: ScheduleModel = <ScheduleModel>this.draggable.shift;
        this.employeeActionService.handleCreate(
          team.department_id,
          team.id,
          this.mode === 'employee' ? shift.user_id : undefined,
          shift.date,
        );
        break;
      }
      case DragType.OPEN_SHIFT: {
        const shift: OpenShiftModel = <OpenShiftModel>this.draggable.shift;
        this.openShiftActionService.handleCreate(shift.department_id, shift.team_id, shift.date);
        break;
      }
      case DragType.REQUIRED_SHIFT: {
        const shift: RequiredShiftModel = <RequiredShiftModel>this.draggable.shift;
        this.requiredshiftActionService.handleCreate(shift.date, shift.department_id, shift.team_id);
        break;
      }
      case DragType.EVENT: {
        const eventItem: EventModel = <EventModel>this.draggable.shift;
        this.eventActionService.handleCreate(eventItem.date, eventItem.department_id);
        break;
      }
    }
  }

  delete(event: MouseEvent) {
    event.stopPropagation();
    this.onDelete.emit();
  }
}
